import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Gift, Plus, Trash2, X, Mail, CheckCircle2 } from "lucide-react";

const EMPTY = { donor_name: "", donor_email: "", amount: "", date: "", message: "", send_receipt: true };
const fmtEur = (n) => (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("fr-FR") : "—");

export default function ClubDonations() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ count: 0, total: 0, this_month: 0 });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/clubs/me/donations"); setItems(data.items || []); setStats(data.stats || {}); }
    catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.donor_name.trim()) { toast.error("Nom du donateur requis."); return; }
    if (!form.donor_email || !form.donor_email.includes("@")) { toast.error("Email valide requis."); return; }
    const amount = parseFloat(String(form.amount).replace(",", "."));
    if (!amount || amount <= 0) { toast.error("Montant invalide."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/clubs/me/donations", { ...form, amount });
      toast.success(data.donation.receipt_sent ? "Don enregistré, reçu envoyé par email." : "Don enregistré.");
      setForm(EMPTY); setAdding(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible."); }
    finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer le don de ${d.donor_name} ?`)) return;
    try { await api.delete(`/clubs/me/donations/${d.id}`); toast.success("Don supprimé."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible."); }
  };

  return (
    <div className="max-w-4xl" data-testid="club-donations">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Mes dons</h1>
          <p className="text-slate-500 text-sm mt-1">Enregistrez les dons reçus (via votre lien de don) et envoyez automatiquement un reçu de remerciement au donateur.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setAdding(true); }} className="btn-shine text-white" data-testid="club-donation-add-btn"><Plus size={16} className="mr-1" /> Enregistrer un don</Button>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3">
        {[["Total collecté", fmtEur(stats.total), "total"], ["Ce mois-ci", fmtEur(stats.this_month), "month"], ["Nombre de dons", stats.count ?? 0, "count"]].map(([l, v, key]) => (
          <div key={key} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`club-donation-stat-${key}`}>
            <div className="text-2xl font-extrabold text-rose-600">{v}</div>
            <div className="text-xs text-slate-500 mt-0.5">{l}</div>
          </div>
        ))}
      </div>

      {adding && (
        <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="club-donation-form">
          <div className="flex items-center justify-between"><h2 className="font-heading font-bold">Nouveau don</h2><button onClick={() => setAdding(false)} className="text-slate-400 hover:text-slate-700" data-testid="club-donation-close"><X size={18} /></button></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nom du donateur *</Label><Input value={form.donor_name} onChange={(e) => set("donor_name", e.target.value)} data-testid="club-donation-name" /></div>
            <div><Label>Email du donateur *</Label><Input type="email" value={form.donor_email} onChange={(e) => set("donor_email", e.target.value)} data-testid="club-donation-email" /></div>
            <div><Label>Montant (€) *</Label><Input value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="ex : 50" data-testid="club-donation-amount" /></div>
            <div><Label>Date du don</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="club-donation-date" /></div>
            <div className="sm:col-span-2"><Label>Message personnalisé (facultatif)</Label><Textarea rows={2} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Un mot de remerciement ajouté au reçu…" data-testid="club-donation-message" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.send_receipt} onCheckedChange={(v) => set("send_receipt", v)} data-testid="club-donation-sendreceipt" /> Envoyer un reçu de remerciement par email au donateur</label>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setAdding(false)} data-testid="club-donation-cancel">Annuler</Button><Button onClick={save} disabled={busy} className="btn-shine text-white" data-testid="club-donation-save">{busy ? "Enregistrement…" : "Enregistrer"}</Button></div>
        </div>
      )}

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? <div className="p-6 text-slate-500 text-sm">Chargement…</div> : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="club-donations-empty">
            <Gift size={30} className="mx-auto text-slate-300 mb-2" /> Aucun don enregistré pour l'instant.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                <th className="text-left px-4 py-3">Donateur</th><th className="text-left px-4 py-3">Montant</th>
                <th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Reçu</th><th className="text-right px-4 py-3">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100" data-testid={`club-donation-row-${d.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{d.donor_name}</div>
                      <div className="text-xs text-slate-500">{d.donor_email}</div>
                    </td>
                    <td className="px-4 py-3 font-bold text-rose-600">{fmtEur(d.amount)}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(d.date)}</td>
                    <td className="px-4 py-3">
                      {d.receipt_sent
                        ? <span className="inline-flex items-center gap-1 text-indigo-700 text-xs font-semibold"><CheckCircle2 size={14} /> Envoyé</span>
                        : <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><Mail size={14} /> Non envoyé</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => remove(d)} data-testid={`club-donation-delete-${d.id}`}><Trash2 size={15} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
